import { useEffect, useRef } from 'react'

/**
 * The light around the board while free spins are running.
 *
 * Sun glare, in the sense a lens flare is: a hot band along the cabinet's
 * edge, smooth beams of mixed length standing off it, and fine sparkle in
 * front. Every beam leaves the board's own outline at a right angle, so the
 * light belongs to the cabinet rather than to something parked behind it.
 *
 * Three shapes were tried before this one, and the reasons they failed are
 * worth keeping. A plain soft halo read as haze: on a bright saturated sky a
 * wide pale glow adds no light, it only lifts the sky's luminance and leaves
 * the clouds looking washed out. A conic ray fan read as a separate object,
 * because its spokes came from a centre instead of from the frame. A jagged
 * flame corona read as a drawn silhouette — an outline is a graphic, and the
 * eye reads light by its falloff, not by its edge (`docs/vfx-guide.md`).
 *
 * Drawn into a SMALL canvas — 384x320 for a layer that displays around
 * 1100px wide — and stretched by CSS. Glare is soft, so the browser's
 * bilinear upscale is the blur it needs, for free, and the animation costs a
 * fifth of a megapixel a frame instead of a whole one. It also removes
 * resize handling entirely: the aura box is always 6:5, like the board, so
 * the buffer never changes shape and never has to be reallocated.
 *
 * Geometry is free: `.stage` is a block in a centred flex column, so its box
 * is exactly the board's box, and this layer is that box grown by `PAD`.
 */

/** How far past the board the light reaches, as a fraction of the board. */
const PAD = 0.6

/**
 * The painted edge of the frame art inside its own box, per side, as a
 * fraction of that box's width (left/right) or height (top/bottom).
 *
 * The light has to stand on the painted edge, not on the element box: the
 * frame PNG is `contain` in a 6:5 shell and carries transparent margin all
 * round, so an outline drawn at the box edge floats visibly off the cabinet.
 *
 * The four numbers are NOT the same, which is the whole reason this is a
 * table and not one constant. Assuming a uniform margin — 2.891%, the figure
 * the free-spins badge is placed against — put the glow on the frame top and
 * left a 17px gap down each side, because the artwork's side margins are
 * half again as wide as its top and bottom ones. Measured off the PNG's
 * alpha channel at a low threshold: the outer edge is antialiased, and the
 * eye takes the first pixel that is visibly there, not the first opaque one.
 */
const EDGE = { left: 0.03516, right: 0.03646, top: 0.02891, bottom: 0.02578 }

/** Corner radius of the painted frame, as a fraction of its painted width. */
const CORNER = 0.0625

/** Where the board's own edge falls inside this (larger) box. */
const F = PAD / (1 + 2 * PAD)

const MOTES = 52

/** Buffer size. 6:5, the same as the aura box, so nothing is ever squashed. */
const BUF_W = 384
const BUF_H = 320

/** Resolution of the outline the beams stand on. */
const STEPS = 720

/** Beams around the board. */
const BEAMS = 84

interface Sample {
  x: number
  y: number
  /** Outward unit normal of the outline at this point. */
  nx: number
  ny: number
}

/**
 * The board's outline as a rounded rectangle, walked by ARC LENGTH rather
 * than by segment. Giving every side the same number of samples packs them
 * tightly on the short sides and stretches them on the long ones, and the
 * beams then come out visibly denser top and bottom than left and right —
 * the one thing that gives a procedural glare away as procedural.
 */
function outline(x0: number, y0: number, w: number, h: number, r: number): Sample[] {
  const eh = w - 2 * r
  const ev = h - 2 * r
  const arc = (Math.PI / 2) * r
  const total = 2 * eh + 2 * ev + 4 * arc
  const out: Sample[] = []
  for (let i = 0; i < STEPS; i++) {
    let d = (i / STEPS) * total
    let x = 0
    let y = 0
    let nx = 0
    let ny = 0
    // Top edge, round the corner, down the right, and so on. Each arc is
    // walked as angle = distance / radius, which is what keeps the spacing
    // even where the outline turns.
    if (d < eh) {
      x = x0 + r + d
      y = y0
      ny = -1
    } else if ((d -= eh) < arc) {
      const a = -Math.PI / 2 + d / r
      nx = Math.cos(a)
      ny = Math.sin(a)
      x = x0 + w - r + nx * r
      y = y0 + r + ny * r
    } else if ((d -= arc) < ev) {
      x = x0 + w
      y = y0 + r + d
      nx = 1
    } else if ((d -= ev) < arc) {
      const a = d / r
      nx = Math.cos(a)
      ny = Math.sin(a)
      x = x0 + w - r + nx * r
      y = y0 + h - r + ny * r
    } else if ((d -= arc) < eh) {
      x = x0 + w - r - d
      y = y0 + h
      ny = 1
    } else if ((d -= eh) < arc) {
      const a = Math.PI / 2 + d / r
      nx = Math.cos(a)
      ny = Math.sin(a)
      x = x0 + r + nx * r
      y = y0 + h - r + ny * r
    } else if ((d -= arc) < ev) {
      x = x0
      y = y0 + h - r - d
      nx = -1
    } else {
      const a = Math.PI + (d - ev) / r
      nx = Math.cos(a)
      ny = Math.sin(a)
      x = x0 + r + nx * r
      y = y0 + r + ny * r
    }
    out.push({ x, y, nx, ny })
  }
  return out
}

/** Seeded, so the light is the same on every spin and every reload. A fresh
 *  draw per render would read as the glare re-shuffling between spins. */
function lcg(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 0x100000000
  }
}

interface Beam {
  /** Index into the outline. */
  at: number
  /** Length and half-width at the root, as fractions of the reach. */
  len: number
  wide: number
  /** Own phase and rate for the breathing, so no two beams pulse together. */
  phase: number
  rate: number
  alpha: number
  /** Sway: how far the beam leans off the normal, and how fast. */
  sway: number
  swayRate: number
  swayPhase: number
}

/**
 * Lengths are drawn from a squared random, which puts most beams short and
 * a few far out. An even spread reads as a gear wheel; glare reads as glare
 * because a handful of long shafts carry past a bed of short ones.
 */
const beams: Beam[] = (() => {
  const rnd = lcg(0xa1a2e5)
  return Array.from({ length: BEAMS }, (_, i) => {
    // Spread around the outline in order, with jitter inside each slot.
    // Independent draws clump, and a clump of beams reads as one wide
    // wedge — the gaps between them are what makes them beams at all.
    const slot = (i + 0.15 + rnd() * 0.7) / BEAMS
    const long = Math.pow(rnd(), 2)
    return {
      at: Math.floor(slot * STEPS) % STEPS,
      len: 0.3 + long * 0.7,
      // Long beams stay narrow: width and length growing together makes
      // wedges, and a wedge is a shape, not a shaft of light.
      wide: 0.03 + rnd() * 0.05,
      phase: rnd() * Math.PI * 2,
      // 2.4-6.3s a breath. The first pass ran at 4-12s with a shallow
      // amplitude and nobody could see it move: light this soft has no edge
      // to track, so a change slower than a spin does not register as
      // motion at all — it just looks like a slightly different picture
      // every time you glance back.
      rate: 1 + rnd() * 1.6,
      alpha: 0.22 + rnd() * 0.3,
      // Up to 7 degrees off the normal. Length alone reads as a lamp being
      // dimmed; a beam that also leans reads as light moving through air,
      // and the lean is what the eye actually catches.
      sway: (0.02 + rnd() * 0.1) * (rnd() < 0.5 ? -1 : 1),
      swayRate: 0.7 + rnd() * 1.3,
      swayPhase: rnd() * Math.PI * 2,
    }
  })
})()

function drawGlare(ctx: CanvasRenderingContext2D, t: number): void {
  // The shell box, then the painted edge inside it.
  const sx = F * BUF_W
  const sy = F * BUF_H
  const sw = BUF_W - 2 * sx
  const sh = BUF_H - 2 * sy
  const x0 = sx + EDGE.left * sw
  const y0 = sy + EDGE.top * sh
  const w = sw * (1 - EDGE.left - EDGE.right)
  const h = sh * (1 - EDGE.top - EDGE.bottom)
  const pts = outline(x0, y0, w, h, w * CORNER)
  // The shortest way out of the buffer. Beams are the same length on every
  // side, so the tightest side sets the budget — sizing each side to its own
  // clearance would make the top and bottom visibly longer than the flanks.
  const reach = Math.min(x0, y0)

  ctx.clearRect(0, 0, BUF_W, BUF_H)
  ctx.globalCompositeOperation = 'lighter'

  for (const b of beams) {
    const p = pts[b.at]
    // Breathing in LENGTH as well as in alpha. A beam that only dims looks
    // like a light being turned down; one that also shortens looks like the
    // air in front of it moving, which is what glare actually does.
    const pulse = 0.45 + 0.55 * Math.sin(t * b.rate + b.phase)
    const len = reach * b.len * pulse
    const wide = reach * b.wide
    // The beam leans off the normal and back. Rotating the direction rather
    // than the whole shape keeps the root planted on the frame: a beam that
    // slid along the edge as it tilted would read as the light sliding, and
    // this light is supposed to be coming OUT of the cabinet.
    const lean = b.sway * Math.sin(t * b.swayRate + b.swayPhase)
    const cos = Math.cos(lean)
    const sin = Math.sin(lean)
    const nx = p.nx * cos - p.ny * sin
    const ny = p.nx * sin + p.ny * cos
    // Tangent, for the two sides of the beam.
    const tx = -ny
    const ty = nx
    // The root sits slightly INSIDE the frame, so the beam emerges from
    // under the cabinet instead of starting at a visible line beside it.
    const rx = p.x - p.nx * 2
    const ry = p.y - p.ny * 2
    const ex = p.x + nx * len
    const ey = p.y + ny * len
    // Widening toward the tip: parallel sides read as a bar, and the eye
    // takes a slight spread as distance.
    const w1 = wide * 1.9

    const g = ctx.createLinearGradient(rx, ry, ex, ey)
    g.addColorStop(0, `rgba(255, 246, 214, ${b.alpha * pulse})`)
    g.addColorStop(0.28, `rgba(255, 206, 96, ${b.alpha * 0.55 * pulse})`)
    g.addColorStop(1, 'rgba(255, 150, 30, 0)')
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.moveTo(rx + tx * wide, ry + ty * wide)
    ctx.lineTo(ex + tx * w1, ey + ty * w1)
    ctx.lineTo(ex - tx * w1, ey - ty * w1)
    ctx.lineTo(rx - tx * wide, ry - ty * wide)
    ctx.closePath()
    ctx.fill()
  }

  // The source. Beams standing on bare sky read as pasted on; a hot band
  // along the edge they all leave from is what makes them light coming off
  // the cabinet. Three strokes, wide and faint to narrow and hot — a falloff
  // out of three passes, since no gradient follows a rounded rectangle.
  ctx.beginPath()
  const r = w * CORNER
  ctx.moveTo(x0 + r, y0)
  ctx.arcTo(x0 + w, y0, x0 + w, y0 + h, r)
  ctx.arcTo(x0 + w, y0 + h, x0, y0 + h, r)
  ctx.arcTo(x0, y0 + h, x0, y0, r)
  ctx.arcTo(x0, y0, x0 + w, y0, r)
  ctx.closePath()
  for (const band of [
    { width: 13, fill: 'rgba(255, 168, 40, 0.1)' },
    { width: 6, fill: 'rgba(255, 210, 110, 0.14)' },
    { width: 2.6, fill: 'rgba(255, 246, 214, 0.2)' },
  ]) {
    ctx.lineWidth = band.width
    ctx.strokeStyle = band.fill
    ctx.stroke()
  }
}

interface Mote {
  /** Position in the aura box, as percentages. */
  x: number
  y: number
  size: number
  delay: number
  dur: number
  /** Drift, in px, along the outward normal of the edge it was born on. */
  dx: number
  dy: number
  /** Glints carry crossed beams; motes are plain dust. Two speeds, one layer. */
  glint: boolean
}

function makeMotes(): Mote[] {
  const rnd = lcg(0x5eed1a)
  return Array.from({ length: MOTES }, (_, i) => {
    // Sides in turn rather than at random: four independent draws leave one
    // edge of the cabinet dark often enough to notice.
    const side = i % 4
    const along = rnd()
    const out = 0.012 + rnd() * 0.085
    let x = 0
    let y = 0
    let nx = 0
    let ny = 0
    if (side === 0) {
      x = F + along * (1 - 2 * F)
      y = F - out
      ny = -1
    } else if (side === 1) {
      x = F + along * (1 - 2 * F)
      y = 1 - F + out
      ny = 1
    } else if (side === 2) {
      x = F - out
      y = F + along * (1 - 2 * F)
      nx = -1
    } else {
      x = 1 - F + out
      y = F + along * (1 - 2 * F)
      nx = 1
    }
    const travel = 14 + rnd() * 48
    return {
      x: x * 100,
      y: y * 100,
      // Mostly fine dust with a few real glints: an even spread of sizes
      // reads as a texture, a skewed one as sparkle.
      size: 3 + rnd() * (i % 4 === 0 ? 10 : 4),
      delay: rnd() * 3.4,
      dur: 2.4 + rnd() * 3,
      dx: nx * travel,
      // Embers rise: even the ones leaving sideways lean upward a little,
      // which is what separates sparks from a mechanical radial spray.
      dy: ny * travel - 10 - rnd() * 14,
      glint: i % 4 === 0,
    }
  })
}

const motes = makeMotes()

export function BonusAura(): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return

    // Decorative motion only: someone who asked the OS for less of it gets
    // the glare standing still, at one moment of the pulse.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      drawGlare(ctx, 0)
      return
    }

    let raf = 0
    let t0 = 0
    const frame = (now: number): void => {
      t0 ||= now
      drawGlare(ctx, (now - t0) / 1000)
      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <div className="bonus-aura" aria-hidden="true">
      <canvas className="bonus-aura__glare" ref={canvasRef} width={BUF_W} height={BUF_H} />
      <div className="bonus-aura__motes">
        {motes.map((m, i) => (
          <span
            key={i}
            className={`bonus-aura__mote${m.glint ? ' bonus-aura__mote--glint' : ''}`}
            style={
              {
                left: `${m.x}%`,
                top: `${m.y}%`,
                '--au-size': `${m.size}px`,
                '--au-delay': `${m.delay}s`,
                '--au-dur': `${m.dur}s`,
                '--au-dx': `${m.dx}px`,
                '--au-dy': `${m.dy}px`,
              } as React.CSSProperties
            }
          />
        ))}
      </div>
    </div>
  )
}
